import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { trackInternalEvent } from '@rio/tracking'

import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import Icon24 from 'builder/components/Icon'

import { InfoBox, SuccessBox } from '../../'
import { ActionButton, WidgetBox, WidgetContainer } from '../../widget.styles'
import { WidgetProps } from '../../widget.types'
import { WidgetTheme } from '../../WidgetTheme'

import { useThemeConfig } from '../../hooks/useThemeConfig'
import { useFirst90DaysPlanConfig } from './hooks/useFirst90DaysPlanConfig'

import { ActionButtons } from './ActionButtons'
import * as Styles from './styles'

import AnimationVideo from './assets/coaches.webm'
import AnimationVideoMobile from './assets/coaches-mobile.webm'
import ProgressImage from './assets/ilustration.png'
import ProgressMobileImage from './assets/ilustration-mobile.png'

export const First90DaysPlan = ({ service }: WidgetProps) => {
  const { isPhone } = useMediaQueries()
  const theme = useThemeConfig({ service })
  const { text, status, steps, handleCloseModal } = useFirst90DaysPlanConfig(service)

  const isCompleted = status === 'completed'
  const src = isPhone ? ProgressMobileImage : ProgressImage
  const animationSrc = isPhone ? AnimationVideoMobile : AnimationVideo

  const closeModal = () => {
    trackInternalEvent('progress_promo_close', { type: 'coaching' })
    handleCloseModal()
  }

  useEffect(() => {
    if (isCompleted) {
      trackInternalEvent('progress_score_completed', { score: service.title })
    }
  }, [isCompleted, service.title])

  const animationComponent = () => {
    if (isCompleted) return <SuccessBox />
    if (status === 'progress')
      return <Styles.Image src={src} data-testid="first-90-days-plan-image-progress" />

    return (
      <Styles.AnimationVideoContainer data-testid="first-90-days-plan-animation-container-initial">
        <Styles.AnimationVideo src={animationSrc} autoPlay muted loop playsInline />
      </Styles.AnimationVideoContainer>
    )
  }

  return (
    <WidgetTheme theme={theme[status]} tipOrder={service.widgetTipOrder}>
      {status === 'initial' && (
        <Styles.CloseButton onClick={closeModal} data-testid="first-90-days-plan-close-button">
          <Icon24.Close />
        </Styles.CloseButton>
      )}
      <WidgetContainer>
        <WidgetBox>
          <InfoBox
            text={text}
            isCompleted={isCompleted}
            data-testid={`first-90-days-plan-info-${status}`}
          >
            {isCompleted ? (
              <ActionButton
                as={Link}
                to={service.getNextActionUrl()}
                data-testid="first-90-days-plan-action-button-completed"
              >
                {text.actionText}
              </ActionButton>
            ) : (
              <ActionButtons
                steps={steps}
                text={text}
                status={status}
                service={service}
                handleCloseModal={handleCloseModal}
              />
            )}
          </InfoBox>
        </WidgetBox>
        <WidgetBox>{animationComponent()}</WidgetBox>
      </WidgetContainer>
    </WidgetTheme>
  )
}
